import {useCallback, useEffect, useMemo, useState} from 'react';

import {AuthContext} from '../contexts/AuthContext';
import {useServiceWorker} from '../contexts/useServiceWorker';
import { clearStorage } from '../utilities/Storage';
import {
    cacheUser,
    clearTokenStorage,
    clearUserStorage,
    keycloak,
    login,
    logout,
    readCachedUser,
} from './authentication';
import { apiUrl, get, isTest } from '../utilities/SimpleClient';
import { useKeycloakFeatureFlag } from './AuthenticationFeatureSwitch';

const isDevMode = isTest();
const apiRoot = apiUrl();

if ('' === apiRoot) {
    throw new Error('Configuration Error: Plato API url is missing or empty');
}

const readKeycloakMessageFromErrorResponse = async (e) => {
    const response = e.response ?? undefined;
    if (400 === response.status) {
        try {
            const keycloakErrorResponse = await response.json();
            if ('error_description' in keycloakErrorResponse) {
                return keycloakErrorResponse.error_description;
            }

            return 'Authentication error, please try again.';
        } catch (e) {
            // fall through.
        }
    }

    return 'Failed to authenticate user.';
};

export function Auth({
    children,
    fallback,
}) {
    const [ user, setUser ] = useState(undefined);
    const [ error, setError ] = useState(undefined);
    const [ initialised, setInitialised ] = useState(false);

    const { hasPendingUpdate } = useServiceWorker();

    const keycloakFeatureFlag = useKeycloakFeatureFlag();

    const userId = user?.id;
    const isAuthenticated = !!userId;
    const isLoading = isAuthenticated || !initialised;

    const handleLogin = useCallback((options) => {
        if (hasPendingUpdate) {
            return;
        }

        // Clear leftover session data from local storage before logging in again.
        clearUserStorage();

        if (keycloakFeatureFlag.useLegacy) {
            if (options.isAuthenticatedViaLegacyPath) {
                setUser(options.user);
                setInitialised(true);

                // Redirect to landing after login.
                window.location.replace(options.redirectUri ?? '/');
                return;
            }

            // Send the user to the login page.
            window.location.replace(`${ window.location.origin }/login`);

            return;
        }

        login(options);
    }, [ keycloakFeatureFlag.useLegacy, hasPendingUpdate ]);

    const handleLogout = useCallback(() => {

        clearStorage();
        clearUserStorage();

        // Instead of returning users to a logged out landing page, we now have a landing login page
        // which shows a "Login" button that initiates the Keycloak authentication when pressed.
        const logoutLandingPage = window.location.origin;

        if (keycloakFeatureFlag.useLegacy) {
            setUser(undefined);
            clearTokenStorage();
            window.location.replace(logoutLandingPage);
            return;
        }

        logout({
            redirectUri: logoutLandingPage,
        });
    }, [ keycloakFeatureFlag.useLegacy ]);

    const authenticationContext = useMemo(() => ({
        login: handleLogin,
        logout: handleLogout,
        user,
        userId,
        isAuthenticated,
        // Backward compatibility with existing code.
        isLoading,
        isLoggedIn: isAuthenticated,
    }), [ handleLogin, handleLogout, user, userId, isAuthenticated, isLoading ]);

    useEffect(() => {
            if (initialised || keycloak.didInitialize) {
                // Guard against keycloak/keycloak#12745 to prevent the init being called multiple times.
                // We keep the flag in the component's state to sync the initial load.
                return;
            }

            let cancelled = false;
            const resolveCurrentUserHelper = async () => {
                const cachedUser = readCachedUser();
                if (cachedUser) {
                    return cachedUser;
                }

                const freshUser = await get('whoami');
                cacheUser(freshUser);

                return freshUser;
            };

            const authenticateClientWithKeycloak = async () => {

                // NOTE: Defer state updates until the end of this async operation. Since this runs outside React's
                // synchronous flow, any state change may trigger an immediate re-render of dependent components.
                // This happens frequently whenever we await between multiple updates.

                if (cancelled) {
                    return;
                }

                try {
                    const authResult = await keycloak.init({
                        onLoad: 'check-sso',
                        silentCheckSsoRedirectUri: `${ window.location.origin }/silent-check-sso.html`,
                        pkceMethod: 'S256',
                        enableLogging: isDevMode,
                        useNonce: !isDevMode,
                        // Because our Keycloak instance lives on a different domain this has to be false or the
                        // browser third-party cookie policy will result in periodic full-page refreshes.
                        checkLoginIframe: false,
                    });

                    if (!authResult) {
                        setInitialised(true);
                        return;
                    }

                    const currentUser = await resolveCurrentUserHelper();

                    setUser(currentUser);
                    setInitialised(true);
                } catch (e) {
                    if (cancelled) {
                        throw e;
                    }

                    setError(await readKeycloakMessageFromErrorResponse(e));
                    throw e;
                }
            };

            if (keycloakFeatureFlag.useLegacy) {

                // Nothing to do here except for skipping Keycloak auth, however, we do clear the hooks in case
                // we detected the authentication flag switch at runtime and ended up in an inconsistent state.

                // The actual legacy authentication is handled by the Login component, which notifies this
                // provider with a call to login({ isAuthenticatedViaLegacyPath, user })

                const cachedUser = readCachedUser();
                if (cachedUser) {
                    setUser(cachedUser);
                    setInitialised(true);
                    return;
                }

                // We'll defer until the user has submtited the login button.
                setInitialised(false);
                return () => {
                    cancelled = true;

                    keycloak.onAuthLogout = undefined;
                    keycloak.onTokenExpired = undefined;
                };
            }

            // NOTE: Though this doesn't strictly belong here, it keeps all keycloak-specific features
            // confined into this provider.
            keycloak.onTokenExpired = () => {
                keycloak.updateToken(-1)
                    .catch(() => {
                        if (cancelled) {
                            return;
                        }

                        setError(`Your session has expired. If you're not signed-in automatically please refresh the page.`);
                    });
            };

            keycloak.onAuthLogout = () => {
                if (cancelled) {
                    return;
                }

                setUser(undefined);
            };

            authenticateClientWithKeycloak();

            return () => {
                cancelled = true;

                keycloak.onAuthLogout = undefined;
                keycloak.onTokenExpired = undefined;
            };
        },
        // NOTE: Suppress badly misleading eslint suggestion. This must execute once, on mount.
        // eslint-disable-next-line
        [],
    );

    if (error) {
        return (
            <div>Authentication Error: { error }</div>
        );
    }

    if (keycloakFeatureFlag.useKeycloak && !initialised) {
        // In the Keycloak case, we can't show any components until the authentication
        // provider is fully initialized after coming back from the redirect.
        return fallback ? fallback() : null;
    }

    return (
        <AuthContext.Provider value={ authenticationContext }>
            { children }
        </AuthContext.Provider>
    );
}
